// JSX port of the companion's renderMessages. READ-ONLY preview — the spark's card
// links to the patient chat.html; here it is a plain <div> with NO outbound
// navigation (a coordinator preview must not open the patient's chat surface, and
// must not fire recordChatView / any read-marking write). Staff avatars are inline-icon
// placeholders (the spark's image assets live in the patient spark); Ava's is AvaAvatar.
// The unread count is PATIENT-relative (unreadForPatient) — labelled as such.
//
// FIDELITY LIMIT: outreach_tail carries no visible_to_patient flag, so this may
// surface rows the patient wouldn't see. author_lastname is stripped upstream
// (stripOutreachAuthorLastName); this is cosmetic, not an authz boundary.

import React from 'react';
import { timeAgo, chatPreviewText } from '@/logic/patientHome';
import {
  chatSender, isSpruceRow, spruceAuthorFirstName, AVA_NAME, AVA_ROLE,
} from '@/logic/chatMessage';
import AvaAvatar from '@/components/AvaAvatar';
import { Icon } from './icons';

function sourceMeta(row) {
  const { kind } = chatSender(row);
  if (isSpruceRow(row) && kind !== 'patient') return { label: spruceAuthorFirstName(row) || 'Clinic line', cls: 'staff', icon: 'withings' };
  if (kind === 'ava') {
    return {
      label: <><span style={{ color: 'var(--text-primary)' }}>{AVA_NAME}</span> – {AVA_ROLE}</>,
      cls: 'system',
      ava: true,
    };
  }
  if (kind === 'patient') return { label: 'You', cls: 'staff', icon: 'withings' };
  return { label: row.author_firstname || 'Your care team', cls: 'staff', icon: 'withings' };
}

/** @param {{ outreach: any[], unread?: { count: number, more: boolean } | null, unreadUnknown?: boolean }} props */
export default function CareTeamMessages({ outreach, unread: unreadState = null, unreadUnknown = false }) {
  const rows = (outreach || [])
    .slice()
    .sort((a, b) => new Date(b.sent_at || b.created_at || 0) - new Date(a.sent_at || a.created_at || 0));

  const unread = unreadState?.count ?? 0;
  const unreadText = `${unread}${unreadState?.more ? '+' : ''} unread for patient`;
  // A failed marker read must not read as "nothing unread", which is what the companion's idle line implies.
  let subtitle = 'Monitoring your health daily';
  if (unreadUnknown) subtitle = 'Latest messages';
  else if (unread > 0) subtitle = unreadText;

  const latest = rows[0];
  const meta = latest ? sourceMeta(latest) : null;
  // A badge or lab-results row previews its first line only, as the companion's home card does.
  const preview = latest ? chatPreviewText(latest) : '';

  return (
    <section className="acc-section fade-up" aria-labelledby="ph-messages-title">
      <div className="acc-section__header acc-section__header--care-team">
        <h2 className="acc-section__title" id="ph-messages-title">Your Care Team</h2>
        <p className="acc-care-subtitle">{subtitle}</p>
      </div>
      <div>
        {rows.length === 0 ? (
          <p className="acc-empty-note acc-empty-note--bare">No messages yet</p>
        ) : (
          <>
            {/* Read-only preview — display-only div (spark uses an <a> to chat.html).
                Unread is a single dot; the count stays in the subtitle + aria-label. */}
            <div
              className={`acc-msg-preview${unread > 0 ? ' acc-msg-preview--unread' : ''}`}
              aria-label={`Latest care-team message (preview)${unread > 0 ? `, ${unreadText}` : ''}`}
            >
              {meta.ava ? <AvaAvatar size={40} /> : (
                <div className="acc-msg-preview__avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={meta.icon} size={20} />
                </div>
              )}
              <div className="acc-msg-preview__content">
                <div className="acc-msg-preview__top">
                  <span className={`acc-msg-preview__source acc-msg-preview__source--${meta.cls}`}>{meta.label}</span>
                  <span className="acc-msg-preview__meta">
                    {unread > 0 && <span className="acc-msg-preview__dot" aria-hidden="true" />}
                    <time className="acc-msg-preview__time">{timeAgo(latest.sent_at || latest.created_at)}</time>
                  </span>
                </div>
                <p className="acc-msg-preview__body">{preview}</p>
              </div>
            </div>
            <p className="acc-msg-preview__hint">{rows.length} message{rows.length > 1 ? 's' : ''} from the care team</p>
          </>
        )}
      </div>
    </section>
  );
}
